// Import from a CSV file with field mapping (2026-10-21, docs/IMPORT.md): users, agents, extensions and contacts.
//   1. choose the file: its header row is read here
//   2. map each column of the file to a field (matched by name where it can be; "ignore" drops a column; contacts
//      keep other columns as custom fields)
//   3. users, agents, extensions: "Check" runs the server's check (nothing is created) and lists every row;
//      "Import" sends the file in parts of 100 rows and lists what was created, with what is shown only once
//      (generated passwords, PINs, new phones' SIP accounts), which can be saved as a CSV file
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Select, Checkbox } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useCan } from '@/store/auth';
import { DataTable, ErrorBox } from '@/components/common';
import { api, errorText, ApiError } from '@/lib/api';
import { fieldLabel } from '@/lib/utils';
import { toast } from '@/store/toast';
import { t } from '@/i18n';

export type ImportKind = 'users' | 'agents' | 'extensions' | 'contacts';
const PART = 100;
const IGNORE = '-';
const CUSTOM = '*';

// "a,b\n..." -> rows of cells, as the server reads it (routes/data.ts parseCsv)
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cell = '', q = false;
  text = text.replace(/^﻿/, '');
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}
const csvCell = (v: unknown) => { const s = v === undefined || v === null ? '' : String(v); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
export const csvText = (rows: unknown[][]) => rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
function saveFile(name: string, text: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// other names a column often has in exported files
const ALIASES: Record<string, string[]> = {
  phone_number: ['phone', 'mobile', 'number', 'telephone', 'tel', 'cell', 'phone_no', 'mobile_number'],
  first_name: ['firstname', 'given_name', 'given', 'forename'], last_name: ['lastname', 'surname', 'family_name', 'family'],
  email: ['e-mail', 'mail', 'email_address'], username: ['user', 'login', 'user_name', 'user_id'],
  name: ['full_name', 'display_name', 'agent_name', 'extension_name'], agent_code: ['code', 'agent_id', 'agent_number'],
  owner: ['user', 'username'], groups: ['group', 'permission_groups'], extension: ['ext', 'extension_number'],
  number: ['ext', 'extension', 'extension_number', 'ext_number'], class_of_service: ['cos'], voicemail: ['voicemail_enabled'], timezone: ['time_zone', 'tz'],
};
const norm = (s: string) => s.trim().toLowerCase().replace(/[\s.]+/g, '_');
function autoMap(head: string[], fields: string[], custom: boolean): string[] {
  const used = new Set<string>();
  const out = head.map((h) => { const f = fields.find((x) => x === norm(h)); if (f) used.add(f); return f ?? ''; });
  return out.map((m, i) => {
    if (m) return m;
    const f = fields.find((x) => !used.has(x) && (ALIASES[x] ?? []).includes(norm(head[i])));
    if (f) { used.add(f); return f; }
    return custom ? CUSTOM : IGNORE;
  });
}

interface RowResult { line: number; status: 'READY' | 'CREATED' | 'ERROR'; key: string; id?: number; error?: string; field?: string; created?: Record<string, unknown>; resolved?: Record<string, unknown> }
const rowError = (r: RowResult) => errorText(new ApiError(400, r.error ?? '', r.field ? { field: r.field } : {}));
const RESOLVED: Record<string, string> = { NEW: '(new)', NEXT: '(next free number)', NONE: '(none)' };

export function ImportDialog({ kind, query, onDone }: { kind: ImportKind; query?: Record<string, string | number>; onDone: () => void }) {
  const fields = useQuery({ queryKey: ['import-fields', kind], queryFn: () => api<{ required: string[]; optional: string[]; custom: boolean; max_rows?: number }>(`/import/${kind}/fields`) });
  const [rows, setRows] = React.useState<string[][] | null>(null);
  const [fileName, setFileName] = React.useState('');
  const [map, setMap] = React.useState<string[]>([]);
  const [skipDnc, setSkipDnc] = React.useState(true);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  const [results, setResults] = React.useState<{ check: boolean; rows: RowResult[] } | null>(null);
  const [summary, setSummary] = React.useState<any>(null);
  const all = fields.data ? [...fields.data.required, ...fields.data.optional] : [];
  const custom = !!fields.data?.custom;

  async function choose(f: File | undefined) {
    setError(null); setResults(null); setSummary(null);
    if (!f) { setRows(null); return; }
    const r = parseCsv(await f.text());
    setFileName(f.name);
    setRows(r);
    setMap(r.length ? autoMap(r[0], all, custom) : []);
  }
  const head = rows?.[0] ?? [];
  const data = rows ? rows.slice(1) : [];
  const mapping = Object.fromEntries(head.map((h, i) => [h.trim(), map[i] === IGNORE ? '' : map[i] === CUSTOM ? norm(h) : map[i]]));
  const missing = (fields.data?.required ?? []).filter((f) => !map.includes(f));
  const twice = map.filter((m, i) => m !== IGNORE && m !== CUSTOM && map.indexOf(m) !== i);
  const ready = rows && data.length > 0 && !missing.length && !twice.length && !busy;

  async function check() {
    setError(null); setBusy(t('Checking…'));
    try { const r = await api(`/import/${kind}`, { method: 'POST', query: { check: 1 }, body: { csv: csvText(rows!), mapping } }); setResults({ check: true, rows: r.rows }); }
    catch (e) { setError(e); } finally { setBusy(''); }
  }
  async function run() {
    setError(null);
    try {
      if (kind === 'contacts') {
        setBusy(t('Importing…'));
        const r = await api('/contacts/import', { method: 'POST', query: { ...query, skip_dnc: skipDnc ? 1 : 0 }, body: { csv: csvText(rows!), mapping } });
        setSummary(r); toast.ok(t('{{n}} contacts added', { n: r.added }));
        return;
      }
      const out: RowResult[] = [];
      for (let i = 0; i < data.length; i += PART) {
        setBusy(t('Importing rows {{from}} to {{to}} of {{n}}…', { from: i + 1, to: Math.min(i + PART, data.length), n: data.length }));
        const r = await api(`/import/${kind}`, { method: 'POST', query: { line_offset: i }, body: { csv: csvText([head, ...data.slice(i, i + PART)]), mapping } });
        out.push(...r.rows);
        setResults({ check: false, rows: [...out] });
      }
      toast.ok(t('{{n}} created', { n: out.filter((r) => r.status === 'CREATED').length }));
    } catch (e) { setError(e); } finally { setBusy(''); }
  }
  function template() {
    saveFile(`${kind}-template.csv`, csvText([all]));
  }
  function download() {
    const keys = [...new Set((results?.rows ?? []).flatMap((r) => Object.keys(r.created ?? {})))];
    saveFile(`${kind}-import-results.csv`, csvText([['line', 'key', 'status', 'error', 'id', ...keys],
      ...(results?.rows ?? []).map((r) => [r.line, r.key, r.status, r.error ? rowError(r) : '', r.id ?? '', ...keys.map((k) => r.created?.[k] ?? '')])]));
  }

  const n = (s: RowResult['status']) => (results?.rows ?? []).filter((r) => r.status === s).length;
  const createdKeys = [...new Set((results?.rows ?? []).flatMap((r) => Object.keys(r.created ?? {})))];
  return (
    <div className="grid gap-3" data-testid={`import-${kind}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="file" accept=".csv,text/csv,text/plain" className="max-w-sm" data-testid="import-file" onChange={(e) => choose(e.target.files?.[0])} />
        {kind !== 'contacts' && <Button type="button" variant="outline" size="sm" onClick={template}><Download /> {t('Template')}</Button>}
      </div>
      {fields.data && <p className="text-xs text-muted-foreground">
        {t('Required: {{fields}}.', { fields: fields.data.required.map(fieldLabel).join(', ') })} {fields.data.max_rows ? t('Up to {{n}} rows per file.', { n: fields.data.max_rows }) : ''}
      </p>}
      {rows && head.length > 0 && !results && (
        <div className="grid gap-2" data-testid="import-mapping">
          <div className="text-sm font-medium">{t('{{file}}: {{n}} rows. Match the columns to the fields:', { file: fileName, n: data.length })}</div>
          <div className="grid max-h-80 gap-1 overflow-y-auto">
            {head.map((h, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr] items-center gap-2 text-sm">
                <span className="truncate font-medium" title={h}>{h || t('(no name)')}</span>
                <span className="truncate text-xs text-muted-foreground" title={data[0]?.[i]}>{data[0]?.[i] ?? ''}</span>
                <Select aria-label={t('Field for column {{name}}', { name: h })} name={`map_${i}`} value={map[i] ?? IGNORE}
                        onChange={(e) => setMap((m) => m.map((x, j) => (j === i ? e.target.value : x)))}>
                  <option value={IGNORE}>{t('(ignore)')}</option>
                  {custom && <option value={CUSTOM}>{t('Custom field "{{name}}"', { name: norm(h) })}</option>}
                  {all.map((f) => <option key={f} value={f}>{fieldLabel(f)}{fields.data?.required.includes(f) ? ' *' : ''}</option>)}
                </Select>
              </div>
            ))}
          </div>
          {missing.length > 0 && <p className="text-sm text-destructive" data-testid="import-missing">{t('Not matched yet: {{fields}}', { fields: missing.map(fieldLabel).join(', ') })}</p>}
          {twice.length > 0 && <p className="text-sm text-destructive">{t('Matched to two columns: {{fields}}', { fields: [...new Set(twice)].map(fieldLabel).join(', ') })}</p>}
          {kind === 'contacts' && <label className="flex items-center gap-2 text-sm"><Checkbox checked={skipDnc} onChange={(e) => setSkipDnc(e.target.checked)} /> {t('Mark numbers on the DNC list as DNC')}</label>}
        </div>
      )}
      {summary && <div className="rounded-md bg-muted p-3 text-sm" data-testid="import-summary">{t('Added {{added}}, duplicates {{duplicates}}, invalid {{invalid}}, marked DNC {{dnc}}.', { added: summary.added, duplicates: summary.duplicates, invalid: summary.invalid, dnc: summary.marked_dnc })}</div>}
      {results && (
        <div className="grid gap-2" data-testid="import-results">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {results.check ? <Badge variant="info">{t('{{n}} ready', { n: n('READY') })}</Badge> : <Badge variant="success">{t('{{n}} created', { n: n('CREATED') })}</Badge>}
            {n('ERROR') > 0 && <Badge variant="destructive">{t('{{n}} with errors', { n: n('ERROR') })}</Badge>}
            {results.check && <span className="text-xs text-muted-foreground">{t('Nothing was created yet.')}</span>}
            {!results.check && createdKeys.length > 0 && <span className="text-xs text-muted-foreground">{t('Passwords and PINs are shown only now: save the results.')}</span>}
            {!results.check && <Button type="button" size="sm" variant="outline" className="ml-auto" onClick={download}><Download /> {t('Save results')}</Button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            <DataTable rowKey={(r) => r.line} rows={results.rows} columns={[
              { key: 'line', label: t('Line') }, { key: 'key', label: fieldLabel(kind === 'users' ? 'username' : kind === 'agents' ? 'agent_code' : 'number'), render: (r) => r.key || '-' },
              { key: 'status', label: t('Result'), render: (r) => r.status === 'ERROR' ? <span className="text-destructive" data-testid={`row-error-${r.line}`}>{rowError(r)}</span>
                : <Badge variant={r.status === 'CREATED' ? 'success' : 'info'}>{r.status === 'CREATED' ? t('Created') : t('Ready')}</Badge> },
              { key: 'detail', label: t('Details'), render: (r) => <span className="block whitespace-normal break-all text-xs">{Object.entries(r.created ?? r.resolved ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => `${fieldLabel(k)}: ${RESOLVED[String(v)] ? t(RESOLVED[String(v)]) : v}`).join(' · ')}</span> },
            ]} />
          </div>
        </div>
      )}
      {busy && <p className="text-sm text-muted-foreground" data-testid="import-busy">{busy}</p>}
      <ErrorBox error={error} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>{t('Close')}</Button>
        {results?.check && <Button type="button" variant="outline" onClick={() => setResults(null)}>{t('Back to the columns')}</Button>}
        {kind !== 'contacts' && !results && <Button type="button" variant="outline" disabled={!ready} onClick={check}><CheckCircle2 /> {t('Check')}</Button>}
        {(!results || results.check) && !summary && <Button type="button" disabled={!ready} onClick={run}><Upload /> {t('Import {{n}} rows', { n: data.length })}</Button>}
      </div>
    </div>
  );
}

// the "Import" button of a list page: the dialog, then the list is read again
export function ImportButton({ kind, title, right, refresh }: { kind: Exclude<ImportKind, 'contacts'>; title: string; right: string; refresh: string[] }) {
  const allowed = useCan(right);
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  if (!allowed) return null;
  const close = () => { setOpen(false); for (const k of refresh) void qc.invalidateQueries({ queryKey: [k] }); };
  return (<>
    <Button variant="outline" onClick={() => setOpen(true)} data-testid={`import-${kind}-button`}><Upload /> {t('Import')}</Button>
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      {open && <DialogContent title={title} className="max-w-4xl"
        description={t('A CSV file with a header row, for example saved from Excel. Each row is checked and created as the form would; rows with errors are listed and the others are created.')}>
        <ImportDialog kind={kind} onDone={close} />
      </DialogContent>}
    </Dialog>
  </>);
}
